import useNotify from 'hooks/useNotify';
import { useTranslation } from 'react-i18next';
import useUpdateTemplateSettingsMutation from 'components/domain/automation/useUpdateTemplateSettingsMutation';
import useFetchTemplateSettings from 'components/domain/automation/useFetchTemplateSettings';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { TemplateSettingsUpdatePayload } from 'models/automation/TemplateSettings';
import { UsageType } from 'models/automation/Common';
import { typedEntries } from 'utils/funcs';
import { LanguageCode } from 'i18n/internationalization.types';
import useAppModal from 'hooks/useAppModal';
import useModalId from 'components/modal/useModalId';
import { ManageLanguagesModalFormValues } from './ManageLanguagesModal.schema';
import useRemoveLanguagesConfirmationModal from './useRemoveLanguagesConfirmationModal';

const useManageLanguagesFormSubmit = ({
  usageType,
}: {
  usageType: UsageType;
}) => {
  const { closeModal } = useAppModal();
  const modalId = useModalId();
  const { notifyError, notifySuccess } = useNotify();
  const { t } = useTranslation();
  const { data: templateSettings } = useFetchTemplateSettings({ usageType });
  const { openRemoveLanguagesConfirmationModal } =
    useRemoveLanguagesConfirmationModal();
  const { mutateAsync: updateTemplateSettings } =
    useUpdateTemplateSettingsMutation({
      onSuccess: () => {
        notifySuccess(t('pageAutomations.common.manageLanguagesModal.success'));
        closeModal(modalId);
      },
      onError: (error) => {
        notifyError(
          getErrorMessage({
            apiErrorCode: error.response?.data?.apiErrorCode,
            apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
            baseKey:
              'pageAutomations.common.manageLanguagesModal.apiErrorCodes.',
            t,
          }),
        );
      },
    });

  const handleSubmit = async (formValues: ManageLanguagesModalFormValues) => {
    const payload: TemplateSettingsUpdatePayload = {
      usageType,
      locales: typedEntries(formValues.locales)
        .filter(([_, value]) => value)
        .map(([key]) => key as LanguageCode),
      autoTranslate: formValues.autoTranslate,
    };

    const previousLocales = templateSettings?.locales ?? [];
    const nextLocales = payload.locales ?? [];
    const removedLanguages = previousLocales.filter(
      (lang) => !nextLocales.includes(lang),
    );

    if (removedLanguages.length > 0 && usageType === UsageType.CUSTOM) {
      openRemoveLanguagesConfirmationModal({
        removedLanguages,
        onConfirmAsync: async () => {
          await updateTemplateSettings(payload);
        },
      });
    } else {
      await updateTemplateSettings(payload);
    }
  };

  return {
    handleSubmit,
  };
};

export default useManageLanguagesFormSubmit;
